/**
 * Photos panel on the Signal-detail page. Two view modes:
 *
 *   - Events (default) — recent person-episodes for the box
 *     (vision_person_episode where photos_with_person > 0), each
 *     showing up to ~6 AI-confirmed-person photos taken inside the
 *     episode window. This is the camera4 "checkout viewer" shape —
 *     operators want photos that go with WHO walked up to the box,
 *     not the latest frames regardless of content.
 *
 *   - Raw photos — the original flat photo browser, with optional
 *     date-range + "person only" filter. Use when you want to see
 *     EVERY frame regardless of episode pairing (e.g. dim-lit
 *     storeroom debugging).
 *
 * The mode toggle persists per session (sessionStorage) so an
 * operator drilling into multiple boxes doesn't have to flip it
 * every time.
 */
import React, { useCallback, useEffect, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { fmtUtc } from "../lib/format";
import {
  type BoxEvents,
  type BoxPhotos,
  EVENT_PAGE_SIZES,
  PHOTO_PAGE_SIZES,
  fetchEvents,
  fetchPhotos,
} from "../services/boxDetailApi";

import EventMotionPlayer from "./EventMotionPlayer";
import LoadingSpinner from "./LoadingSpinner";
import { Button } from "./ui/button";

const today = (): string => new Date().toISOString().slice(0, 10);
const MODE_STORAGE_KEY = "mttr.photosViewer.mode";

type Mode = "events" | "photos";

function loadMode(): Mode {
  try {
    const v = sessionStorage.getItem(MODE_STORAGE_KEY);
    return v === "photos" ? "photos" : "events";
  } catch {
    return "events";
  }
}

function saveMode(m: Mode): void {
  try { sessionStorage.setItem(MODE_STORAGE_KEY, m); } catch { /* ignore */ }
}

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export const PhotosViewer: React.FC<{ scu: string }> = ({ scu }) => {
  const { token } = useAuth();
  const [mode, setMode] = useState<Mode>(loadMode);

  // -- Events mode state -------------------------------------------
  const [events, setEvents] = useState<BoxEvents | null>(null);
  const [eventsLimit, setEventsLimit] = useState(8);
  const [eventsOffset, setEventsOffset] = useState(0);

  // -- Photos mode state -------------------------------------------
  const [photos, setPhotos] = useState<BoxPhotos | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today);
  const [pageSize, setPageSize] = useState(12);
  const [onlyPerson, setOnlyPerson] = useState(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-event view mode (grid vs motion player). Defaults to "play"
  // for the most-recent event each fetch, "grid" for the rest —
  // matches "you probably want to see the newest checkout first."
  const [eventViewMode, setEventViewMode] = useState<Record<number, "play" | "grid">>({});
  const setEventMode = useCallback((id: number, m: "play" | "grid") => {
    setEventViewMode((prev) => ({ ...prev, [id]: m }));
  }, []);

  const runEvents = useCallback(
    (opts: { from?: string; to?: string; limit?: number; offset?: number }) => {
      if (!token) return;
      setLoading(true);
      fetchEvents(scu, token, opts)
        .then((d) => {
          setEvents(d);
          setError(null);
          // First event auto-opens the player; rest start as grid.
          const initial: Record<number, "play" | "grid"> = {};
          d.events.forEach((ev, i) => {
            initial[ev.camera_person_episode_id] = i === 0 ? "play" : "grid";
          });
          setEventViewMode(initial);
        })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    },
    [scu, token],
  );

  const runPhotos = useCallback(
    (opts: { from?: string; to?: string; limit?: number; offset?: number; onlyPerson?: boolean }) => {
      if (!token) return;
      setLoading(true);
      fetchPhotos(scu, token, opts)
        .then((d) => { setPhotos(d); setError(null); })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    },
    [scu, token],
  );

  // Initial + on SCU/mode change.
  useEffect(() => {
    if (mode === "events") {
      runEvents({ limit: eventsLimit });
    } else {
      runPhotos({ onlyPerson });
    }
  }, [scu, mode]);  // intentional: runs on mode/SCU switch only

  const switchMode = (m: Mode): void => {
    setMode(m);
    saveMode(m);
    setEventsOffset(0);
  };

  // -- Events controls ---------------------------------------------
  const eventsPage = Math.floor(eventsOffset / eventsLimit);
  const eventsPages = events
    ? Math.max(1, Math.ceil(events.total / eventsLimit))
    : 1;
  const goEventsPage = (p: number): void => {
    const newOffset = p * eventsLimit;
    setEventsOffset(newOffset);
    runEvents({
      from: (events?.from ?? from) || undefined,
      to: (events?.to ?? to) || undefined,
      limit: eventsLimit,
      offset: newOffset,
    });
  };
  const searchEvents = (): void => {
    setEventsOffset(0);
    runEvents({
      from: from || to || undefined,
      to: to || undefined,
      limit: eventsLimit,
      offset: 0,
    });
  };
  const eventsRecent = (): void => {
    setFrom("");
    setEventsOffset(0);
    runEvents({ limit: eventsLimit });
  };

  // -- Photos controls (raw mode) ----------------------------------
  const photosIsRange = photos?.mode === "range";
  const photosLimit = photos?.limit ?? pageSize;
  const photosPage = photosIsRange ? Math.floor((photos?.offset ?? 0) / photosLimit) : 0;
  const photosPages = photosIsRange ? Math.max(1, Math.ceil((photos?.total ?? 0) / photosLimit)) : 1;
  const searchPhotos = (): void =>
    runPhotos({ from: from || to, to, limit: pageSize, offset: 0, onlyPerson });
  const togglePerson = (): void => {
    const next = !onlyPerson;
    setOnlyPerson(next);
    if (photos?.mode === "range") {
      runPhotos({ from: photos.from, to: photos.to, limit: photos.limit, offset: 0, onlyPerson: next });
    } else {
      runPhotos({ onlyPerson: next });
    }
  };
  const goPhotosPage = (p: number): void =>
    runPhotos({ from: photos?.from, to: photos?.to, limit: photosLimit, offset: p * photosLimit, onlyPerson });

  // -- Render ------------------------------------------------------
  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
          Photos
          {mode === "events" && events && (
            <span className="ml-2 normal-case font-normal text-xs text-secondary-500">
              {events.total} event{events.total === 1 ? "" : "s"}
              {events.from && events.to && ` in ${events.from} → ${events.to}`}
            </span>
          )}
          {mode === "photos" && photos && (
            <span className="ml-2 normal-case font-normal text-xs text-secondary-500">
              {photos.mode === "range"
                ? `${photos.total} in ${photos.from} → ${photos.to}`
                : "2 most recent"}
              {onlyPerson && <span className="ml-1 text-primary-700">· person only</span>}
            </span>
          )}
        </h2>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 text-xs">
          <button
            type="button"
            onClick={() => switchMode("events")}
            className={
              "px-2 py-1 rounded border " +
              (mode === "events"
                ? "bg-primary-100 border-primary-300 text-primary-700 font-semibold"
                : "bg-white border-secondary-300 text-secondary-600 hover:bg-secondary-50")
            }
            title="Group photos by camera person-episode (default)"
          >
            Events
          </button>
          <button
            type="button"
            onClick={() => switchMode("photos")}
            className={
              "px-2 py-1 rounded border " +
              (mode === "photos"
                ? "bg-primary-100 border-primary-300 text-primary-700 font-semibold"
                : "bg-white border-secondary-300 text-secondary-600 hover:bg-secondary-50")
            }
            title="Flat photo grid, regardless of episode pairing"
          >
            All photos
          </button>
        </div>
      </div>

      {/* Filter row */}
      {mode === "events" ? (
        <div className="flex flex-wrap items-center gap-1.5 text-xs mb-3">
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="border border-secondary-300 rounded px-1.5 py-1"
            title="From (max 1 month before To)"
          />
          <span className="text-secondary-400">→</span>
          <input
            type="date"
            value={to}
            max={today()}
            onChange={(e) => setTo(e.target.value)}
            className="border border-secondary-300 rounded px-1.5 py-1"
          />
          <select
            value={eventsLimit}
            onChange={(e) => setEventsLimit(Number(e.target.value))}
            className="border border-secondary-300 rounded px-1.5 py-1 bg-white"
            title="Events per page"
          >
            {EVENT_PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{n}/page</option>
            ))}
          </select>
          <Button type="button" size="sm" onClick={searchEvents}>Search</Button>
          {events?.from && (
            <button type="button" onClick={eventsRecent} className="text-secondary-500 hover:underline px-1">
              ← recent
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5 text-xs mb-3">
          <label
            className="inline-flex items-center gap-1 text-secondary-700 cursor-pointer select-none"
            title="Show only photos the camera AI flagged as containing a person"
          >
            <input type="checkbox" checked={onlyPerson} onChange={togglePerson} className="accent-primary-600" />
            Person only
          </label>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="border border-secondary-300 rounded px-1.5 py-1"
          />
          <span className="text-secondary-400">→</span>
          <input
            type="date"
            value={to}
            max={today()}
            onChange={(e) => setTo(e.target.value)}
            className="border border-secondary-300 rounded px-1.5 py-1"
          />
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-secondary-300 rounded px-1.5 py-1 bg-white"
            title="Photos per page"
          >
            {PHOTO_PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{n}/page</option>
            ))}
          </select>
          <Button type="button" size="sm" onClick={searchPhotos}>Search</Button>
          {photosIsRange && (
            <button type="button" onClick={() => runPhotos({ onlyPerson })} className="text-secondary-500 hover:underline px-1">
              ← recent
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="mb-2 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Events grid */}
      {mode === "events" && (
        loading && !events ? (
          <LoadingSpinner label="loading events" />
        ) : events && events.events.length > 0 ? (
          <>
            <div className="space-y-4">
              {events.events.map((ev) => {
                const evMode = eventViewMode[ev.camera_person_episode_id] ?? "grid";
                return (
                <div key={ev.camera_person_episode_id} className="rounded border border-secondary-200">
                  <div className="px-3 py-2 border-b border-secondary-200 bg-secondary-50 text-xs flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-semibold text-secondary-800">
                        {fmtUtc(ev.end_time)}
                      </span>
                      <span className="ml-2 text-secondary-600">
                        {fmtDuration(ev.duration_seconds)}
                      </span>
                      {ev.max_persons != null && ev.max_persons > 1 && (
                        <span className="ml-2 text-secondary-600">
                          · {ev.max_persons} people
                        </span>
                      )}
                      <span className="ml-2 text-secondary-500 text-[11px]">
                        · {ev.photos_with_person ?? 0} of {ev.total_photos ?? 0} photos w/ person
                      </span>
                    </div>
                    {ev.photos.length > 0 && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setEventMode(ev.camera_person_episode_id, "play")}
                          className={
                            "px-2 py-0.5 rounded border text-[11px] " +
                            (evMode === "play"
                              ? "bg-primary-100 border-primary-300 text-primary-700 font-semibold"
                              : "bg-white border-secondary-300 text-secondary-600 hover:bg-secondary-50")
                          }
                          title="Watch as a stop-motion sequence"
                        >
                          ▶ Play
                        </button>
                        <button
                          type="button"
                          onClick={() => setEventMode(ev.camera_person_episode_id, "grid")}
                          className={
                            "px-2 py-0.5 rounded border text-[11px] " +
                            (evMode === "grid"
                              ? "bg-primary-100 border-primary-300 text-primary-700 font-semibold"
                              : "bg-white border-secondary-300 text-secondary-600 hover:bg-secondary-50")
                          }
                          title="Photo grid"
                        >
                          ▦ Grid
                        </button>
                      </div>
                    )}
                  </div>
                  {ev.photos.length === 0 ? (
                    <div className="p-3 text-xs italic text-secondary-500">
                      No AI-confirmed-person photos in window
                      {" "}(camera saw a person but no individual frame passed the AI threshold).
                    </div>
                  ) : evMode === "play" ? (
                    <EventMotionPlayer
                      photos={ev.photos}
                      durationSeconds={ev.duration_seconds}
                    />
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3">
                      {ev.photos.map((p, i) => (
                        <a
                          key={`${p.azure_url}:${i}`}
                          href={p.azure_url}
                          target="_blank"
                          rel="noreferrer"
                          className="block group"
                          title={`${p.camera_name ?? "camera"} · ${fmtUtc(p.timestamp)}`}
                        >
                          <img
                            src={p.azure_url}
                            alt={`${p.camera_name ?? "camera"} ${fmtUtc(p.timestamp)}`}
                            loading="lazy"
                            className="w-full h-24 object-cover rounded border border-secondary-200 group-hover:opacity-90"
                          />
                          <div className="text-[10px] text-secondary-500 mt-0.5 truncate">
                            {fmtUtc(p.timestamp)}
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );})}
            </div>
            {eventsPages > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm">
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={eventsPage <= 0}
                  onClick={() => goEventsPage(eventsPage - 1)}
                >← Prev</Button>
                <span className="text-xs text-secondary-500">
                  Page {eventsPage + 1} of {eventsPages}
                </span>
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={eventsPage + 1 >= eventsPages}
                  onClick={() => goEventsPage(eventsPage + 1)}
                >Next →</Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-secondary-500">
            No person-events on this box{events?.from ? ` in ${events.from} → ${events.to}` : ""}.
            <button
              type="button"
              onClick={() => switchMode("photos")}
              className="ml-2 text-primary-600 hover:underline"
            >
              Show all photos instead →
            </button>
          </div>
        )
      )}

      {/* Photos grid (legacy flat mode) */}
      {mode === "photos" && (
        loading && !photos ? (
          <LoadingSpinner label="loading photos" />
        ) : photos && photos.photos.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {photos.photos.map((p, i) => (
                <a
                  key={`${p.azure_url}:${i}`}
                  href={p.azure_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block group"
                  title={`${p.camera_name ?? "camera"} · ${fmtUtc(p.timestamp)}`}
                >
                  <img
                    src={p.azure_url}
                    alt={`${p.camera_name ?? "camera"} ${fmtUtc(p.timestamp)}`}
                    loading="lazy"
                    className="w-full h-28 object-cover rounded border border-secondary-200 group-hover:opacity-90"
                  />
                  <div className="text-[11px] text-secondary-500 mt-0.5 truncate">
                    {p.camera_name ? `${p.camera_name} · ` : ""}{fmtUtc(p.timestamp)}
                  </div>
                </a>
              ))}
            </div>
            {photosIsRange && photosPages > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm">
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={photosPage <= 0}
                  onClick={() => goPhotosPage(photosPage - 1)}
                >← Prev</Button>
                <span className="text-xs text-secondary-500">
                  Page {photosPage + 1} of {photosPages}
                </span>
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={photosPage + 1 >= photosPages}
                  onClick={() => goPhotosPage(photosPage + 1)}
                >Next →</Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-secondary-500">
            {photos?.mode === "range" ? `No photos in ${photos.from} → ${photos.to}` : "No recent photos"}
            {onlyPerson ? " with a person detected." : "."}
          </div>
        )
      )}
    </section>
  );
};

export default PhotosViewer;
